/**
 * EscrowCoverPanel - what a contractor the homeowner hires can be paid from,
 * and the action that adds to it.
 *
 * Rendered by `HoTeamTab`, where the homeowner hires contractors. Adding funds
 * runs through `useProjectFunding`, the same checkout-and-verify orchestration
 * milestone funding uses.
 */

import * as React from "react";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { StudioButton } from "@/features/studio/theme";
import { useProjectFunding } from "../../hooks/use-project-funding";
import {
	Btn,
	Eyebrow,
	FloatPanel,
	formatINR,
	Icn,
	secondaryBtnStyle,
} from "./design-primitives";

/**
 * A hire or contractor payout the backend refused because the escrow does not
 * cover it: its message, and the shortfall in rupees.
 */
export interface EscrowShortfallRefusal {
	message: string;
	shortfall: number;
}

interface EscrowCoverPanelProps {
	projectId: string;
	/** Rupees, from the payments summary. */
	availableForHire: number;
	/** Opens the form pre-filled with its shortfall, and shows its message. */
	refusal: EscrowShortfallRefusal | null;
	/** The form was closed without adding funds. */
	onDismissRefusal: () => void;
	/** Run once the server confirms the top-up: the caller's refetch. */
	onFunded: () => Promise<void>;
}

/**
 * The rupee figure typed into the amount field, or `null` when it is not a
 * whole number of rupees or is below the backend's lower bound, Rs. 1
 * (`MIN_TOP_UP_RUPEES`). Above its upper bound, the backend's refusal says so.
 */
function rupeesOf(input: string): number | null {
	if (input.trim() === "") return null;
	const rupees = Number(input);
	return Number.isInteger(rupees) && rupees >= 1 ? rupees : null;
}

export function EscrowCoverPanel({
	projectId,
	availableForHire,
	refusal,
	onDismissRefusal,
	onFunded,
}: EscrowCoverPanelProps) {
	// A designer never adds funds to the escrow, and a contractor they hire is
	// paid by them rather than from it, so neither the figure nor the action is
	// theirs. The route already sends a designer to a different page; this
	// keeps the panel homeowner-only wherever it is rendered.
	const userType = useAuthStore((state) => state.user?.userType);
	const { isFunding, addFunds } = useProjectFunding();
	const [isOpen, setIsOpen] = React.useState(false);
	const [amount, setAmount] = React.useState("");
	const panelRef = React.useRef<HTMLDivElement>(null);
	const amountId = React.useId();

	React.useEffect(() => {
		if (!refusal) return;
		setIsOpen(true);
		setAmount(String(refusal.shortfall));
		// jsdom has no `scrollIntoView`.
		panelRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
	}, [refusal]);

	if (userType !== "homeowner") return null;

	const amountInRupees = rupeesOf(amount);

	const close = (): void => {
		setIsOpen(false);
		setAmount("");
		onDismissRefusal();
	};

	const submit = async (): Promise<void> => {
		if (amountInRupees === null) return;
		await addFunds({
			projectId,
			amount: amountInRupees,
			onFunded: async () => {
				setIsOpen(false);
				setAmount("");
				await onFunded();
			},
		});
	};

	return (
		<div ref={panelRef}>
			<FloatPanel elev={1} padding={22}>
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "flex-start",
						gap: 16,
						flexWrap: "wrap",
					}}
				>
					<div style={{ flex: 1, minWidth: 220 }}>
						<Eyebrow>ESCROW · AVAILABLE FOR HIRING</Eyebrow>
						<div
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 500,
								fontSize: 26,
								color: "var(--color-dashboard-text)",
								letterSpacing: "-0.02em",
								marginTop: 6,
								fontVariantNumeric: "tabular-nums",
							}}
						>
							{formatINR(availableForHire)}
						</div>
						<div
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 12.5,
								lineHeight: 1.5,
								color: "var(--color-dashboard-muted-dark)",
								marginTop: 4,
								maxWidth: 520,
							}}
						>
							What a contractor you hire can be paid from: your project's
							escrow, less the money funded for your designer's milestones and
							the contractors you have hired and not yet paid.
						</div>
					</div>
					{!isOpen && (
						<StudioButton
							variant="secondary"
							size="sm"
							onClick={() => setIsOpen(true)}
							style={secondaryBtnStyle("sm")}
						>
							<Icn name="wallet" size={13} />
							Add funds
						</StudioButton>
					)}
				</div>

				{isOpen && (
					<div
						style={{
							marginTop: 16,
							paddingTop: 16,
							borderTop: "1px solid var(--color-dashboard-border)",
						}}
					>
						{refusal && (
							<div
								role="alert"
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 12.5,
									lineHeight: 1.5,
									color: "var(--color-dashboard-pill-awaiting)",
									marginBottom: 12,
									maxWidth: 620,
								}}
							>
								{refusal.message}
							</div>
						)}
						<label
							htmlFor={amountId}
							style={{
								display: "block",
								fontFamily: "var(--font-body)",
								fontSize: 12.5,
								fontWeight: 600,
								color: "var(--color-dashboard-text)",
								marginBottom: 6,
							}}
						>
							Amount to add (₹)
						</label>
						<div
							style={{
								display: "flex",
								gap: 10,
								alignItems: "center",
								flexWrap: "wrap",
							}}
						>
							<input
								id={amountId}
								type="number"
								inputMode="numeric"
								min="1"
								step="1"
								value={amount}
								onChange={(event) => setAmount(event.target.value)}
								disabled={isFunding}
								style={{
									height: 36,
									width: 180,
									padding: "0 12px",
									borderRadius: 6,
									border: "1px solid var(--color-dashboard-border)",
									background: "var(--color-dashboard-paper)",
									fontFamily: "var(--font-body)",
									fontSize: 13.5,
								}}
							/>
							<Btn
								variant="primary"
								size="sm"
								onClick={() => void submit()}
								disabled={isFunding || amountInRupees === null}
							>
								{isFunding ? "Adding funds..." : "Add funds"}
							</Btn>
							<Btn
								variant="ghost"
								size="sm"
								onClick={close}
								disabled={isFunding}
							>
								Cancel
							</Btn>
						</div>
					</div>
				)}
			</FloatPanel>
		</div>
	);
}
